import React from 'react';
import moment from 'moment';
import { Activity } from 'types/order';
import { formatStatusText, capitalize } from 'utils';
import { ClockIcon } from '@heroicons/react/solid';
import { useNavigate } from 'react-router-dom';

type ActivitiesTimelineProps = {
  activities?: Activity[] | null;
  title?: string;
  claims?: { id: string }[] | null;
};

export const ActivitiesTimeline: React.FC<ActivitiesTimelineProps> = ({
  activities,
  title = 'Order Activity',
  claims,
}) => {
  const navigate = useNavigate();
  const list = Array.isArray(activities) ? [...activities] : [];
  // Sort by createdAt descending (newest to older)
  list.sort((a, b) => {
    const at = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bt = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bt - at;
  });

  if (!list.length) {
    return null;
  }

  // Group activities by date
  const groupedActivities: { date: string; activities: Activity[] }[] = [];
  let currentDate = '';

  list.forEach((activity) => {
    const activityDate = activity.createdAt
      ? moment(activity.createdAt).format('MMMM D, YYYY')
      : 'Unknown date';

    if (activityDate !== currentDate) {
      currentDate = activityDate;
      groupedActivities.push({ date: activityDate, activities: [] });
    }

    groupedActivities[groupedActivities.length - 1].activities.push(activity);
  });

  const toLabel = (eventType?: string | null) => {
    if (!eventType) return 'Updated';

    // Map event types to professional descriptions
    const eventLabels: Record<string, string> = {
      // Claim events
      CLAIM_CREATED: 'Issue filed',
      CLAIM_REFUND_CREATED: 'Refund issued',
      CLAIM_REORDER_CREATED: 'Reorder processed',
      CLAIM_STORE_CREDIT_CREATED: 'Store credit issued',
      CLAIM_DENIED: 'Issue denied',
      CLAIM_RESOLUTION_CANCELLED: 'Resolution cancelled',
      CLAIM_NOTE_ADDED: 'Note added',

      // Order events
      ORDER_CREATED: 'Order placed',
      ORDER_UPDATED: 'Order updated',
      ORDER_CANCELLED: 'Order cancelled',
      ORDER_FULFILLED: 'Order fulfilled',
      ORDER_SHIPPED: 'Order shipped',
      ORDER_DELIVERED: 'Order delivered',
      ADDRESS_UPDATED: 'Shipping address updated',
      PAYMENT_RECEIVED: 'Payment confirmed',
      REFUND_PROCESSED: 'Refund processed',
    };

    return eventLabels[eventType] || capitalize(formatStatusText(eventType));
  };

  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <ClockIcon className="w-4 h-4 text-gray-400" />
      </div>
      <div className="relative">
        {groupedActivities.map((group, groupIdx) => (
          <div key={`date-group-${groupIdx}`}>
            {/* Date Separator */}
            <div className="flex items-center mb-3 mt-4 first:mt-0">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="px-3 text-xs font-medium text-gray-500 uppercase tracking-wide">
                {group.date}
              </span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            {/* Activities for this date */}
            <ul className="">
              {group.activities.map((a, idx) => {
                const isFirstOverall = groupIdx === 0 && idx === 0;
                const isLastInGroup = idx === group.activities.length - 1;
                const isLastOverall =
                  groupIdx === groupedActivities.length - 1 && isLastInGroup;

                return (
                  <li
                    key={`order-activity-${groupIdx}-${idx}`}
                    className="relative pl-10 py-3 group hover:bg-gray-50 rounded-md transition-colors"
                  >
                    {idx !== 0 && (
                      <span
                        className="absolute left-4 top-0 h-1/2 w-px bg-gradient-to-b from-gray-200 to-gray-300"
                        aria-hidden
                      />
                    )}
                    {!isLastOverall && (
                      <span
                        className="absolute left-4 bottom-0 h-1/2 w-px bg-gradient-to-b from-gray-300 to-gray-200"
                        aria-hidden
                      />
                    )}

                    {/* Dot */}
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 -translate-x-1/2">
                      {isFirstOverall ? (
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-60 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600 ring-4 ring-blue-100 shadow" />
                        </span>
                      ) : (
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-gray-400 group-hover:bg-gray-500 transition-colors" />
                      )}
                    </span>

                    {/* Content */}
                    <div className="flex items-center justify-between">
                      <div
                        className={`text-sm text-gray-900 ${
                          isFirstOverall ? 'font-semibold' : ''
                        }`}
                      >
                        {a.eventType?.startsWith('CLAIM') &&
                        claims &&
                        claims.length > 0 ? (
                          <button
                            onClick={() => navigate(`/claims/${claims[0].id}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          >
                            {toLabel(a.eventType)}
                          </button>
                        ) : (
                          <span>{toLabel(a.eventType)}</span>
                        )}
                      </div>
                      <p
                        className={`text-xs text-gray-500 ml-4 whitespace-nowrap ${
                          isFirstOverall ? 'font-semibold' : ''
                        }`}
                      >
                        {a.createdAt
                          ? moment(a.createdAt).format('h:mm A')
                          : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivitiesTimeline;
